import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Button,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core/';

AuctionDocumentDialog.propTypes = {
  guid: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  pluginMode: PropTypes.string.isRequired,
};

const useStyles = makeStyles({
  emailLabel: {
    transform: 'translate(14px, 14px) scale(1)',
  },
  emailField: {
    minHeight: '40px',
  },
  buttons: {
    textAlign: 'center',
  },
  button: {
    backgroundColor: '#1d4d82',
    color: '#ffffff',
    padding: '9px 20px',
    '&:hover': {
      backgroundColor: '#ffffff',
      border: '1px solid #1d4d82',
      color: '#1d4d82',
    },
  },
  error: {
    color: '#FF0000',
  }
});

function AuctionDocumentDialog({guid, displayName, pluginMode}) {
  const classes = useStyles();
  const [emailAddress, setEmailAddress] =  useState('');
  const [loading, setLoading] = useState(false);
  const [badEmail, setBadEmail] = useState(false);
  const [disablebutton, setDisablebutton] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    loading && (isSubmitted || error) ? setLoading(false) : '';
  }, [isSubmitted, error]);

  const handleEmailInput = (event) => {
    const emailRegex = /^([\w+-]+\.)*[\w+-]+@([\w+-]+\.)*[\w+-]+\.[a-zA-Z]{2,4}$/;
    const validatedEmailAddr = emailRegex.test(event.target.value);
    
    setBadEmail(!validatedEmailAddr);
    setEmailAddress(event.target.value);
    validatedEmailAddr ? setDisablebutton(false) : setDisablebutton(true);
  };

  const handleSubmit = async () => {
    if (pluginMode !== 'test') {
      axios.defaults.baseURL = 'https://doc-email-req.auctionservices.workers.dev';
    } else {
      axios.defaults.baseURL = 'https://demo-doc-email-req.auctionservices.workers.dev';
    }

    setLoading(true);

    try {
      const res = await axios.get(`/?action=save-email&emailAddress=${emailAddress}&documentGuid=${guid}`);

      if (res.status === 200 && res.data.status === true) {
        setIsSubmitted(true);
      } else {
        setBadEmail(true);
        console.log(`Error submitting email address:\n ${res.data.message}`);
      }
    } catch (error) {
      setError(true);
    }
  };

  const populateDocMessage = () => {
    return (
      (error && <p className={classes.error}>Unable to submit your request. Please contact us.</p>) || 
      (isSubmitted && <p>Thank you. Please check your email.</p>) || 
      (<p>A link to view <strong>{displayName}</strong> will be emailed to you.</p>)
    );
  };

  const renderForm = () => {
    if (isSubmitted || error) {
      return null;
    } else {
      return (
        <Fragment>
          <Grid item xs={12} sm={12} md={8}>
            <TextField
              required={true}
              autoFocus={true}
              id="document-email-form"
              label="Email Address"
              type="email"
              variant="outlined"
              fullWidth={true}
              error={badEmail}
              helperText={badEmail && 'Please enter a valid email address'}
              placeholder="Enter your email address"
              classes={{
                root: classes.textField,
              }}
              InputLabelProps={{
                classes: {
                  root: classes.emailLabel,
                },
              }}
              InputProps={{
                classes: {
                  root: classes.emailField,
                  input: classes.emailField,
                },
              }}
              onChange={handleEmailInput}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={2} classes={{ item: classes.buttons }}>
            <Button
              variant="contained"
              classes={{
                contained: classes.button,
              }}
              disabled={disablebutton}
              onClick={handleSubmit}
            >
              Submit
              {loading && <CircularProgress size={20} />}
            </Button>
          </Grid>
        </Fragment>
      );
    }
  };

  return (
    <div>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="body1" display="block">
            {populateDocMessage()}
          </Typography>
        </Grid>
        {renderForm()}
      </Grid>
    </div>
  );
}

export default AuctionDocumentDialog;